/**
 * Os quatro KPIs do funil, cada um com a comparacao contra o periodo anterior.
 *
 * Nao usa o `KpiCard` compartilhado de proposito: o quarto cartao precisa de
 * um DETALHAMENTO POR CANAL (ligacao, WhatsApp, e-mail, vendedora) embaixo do
 * valor, e todos precisam de uma linha de DELTA. O numero grande continua sem
 * cor de serie -- identidade de canal vem do icone e do ponto.
 *
 * Regras do delta:
 *  - glifo + texto, sempre ("▲ +12,0% · +48 vs. período anterior"). Cor e
 *    reforco, nunca o unico canal.
 *  - "Abandonaram"/"Venceram" subir nao e bom nem ruim para a regua (e mais
 *    gente para recuperar, mas e mais gente que saiu): o delta fica neutro.
 *    Recuperados, taxa e receita subirem e bom.
 *  - sem periodo anterior ("Desde o inicio") o cartao diz isso, em vez de
 *    inventar "0%".
 *  - a taxa compara "de X% para Y%" em vez de dividir taxa por taxa.
 */

import type { BadgeTone, IconName } from '../../../components/ui';
import { Dot, Icon } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact, formatPercentFromBps, formatRatio } from '../../../lib/format';
import { delta, taxasParaComparar } from '../agregacao';
import { CREDIT_CHANNELS, type Delta, type FunnelKind, type OfferMetrics } from '../types';
import { CANAL, VOCABULARIO } from '../vocabulario';

interface LinhaDeCanal {
  readonly key: string;
  readonly icon: IconName;
  readonly tone: BadgeTone;
  readonly texto: string;
}

const ICONE_TOM: Readonly<Record<'accent' | 'good' | 'info' | 'brand', string>> = {
  accent: 'bg-accent/10 text-accent',
  good: 'bg-status-good/10 text-status-good',
  info: 'bg-info/10 text-info',
  brand: 'bg-brand/10 text-brand',
};

type Sentido = 'neutro' | 'maior-melhor';

function classeDoDelta(diferenca: number, sentido: Sentido): string {
  if (diferenca === 0 || sentido === 'neutro') {
    return 'text-ink-muted';
  }
  return diferenca > 0 ? 'text-status-good' : 'text-status-critical';
}

function glifo(diferenca: number): string {
  if (diferenca > 0) {
    return '▲';
  }
  if (diferenca < 0) {
    return '▼';
  }
  return '▶';
}

function comSinal(texto: string, diferenca: number): string {
  if (diferenca > 0) {
    return `+${texto}`;
  }
  if (diferenca < 0) {
    return `−${texto}`;
  }
  return texto;
}

/** "▲ +12,0% · +48 vs. período anterior". `formatar` recebe o valor ABSOLUTO da diferenca. */
function LinhaDelta({ d, sentido, formatar }: { readonly d: Delta | null; readonly sentido: Sentido; readonly formatar: (abs: number) => string }): JSX.Element {
  if (d === null) {
    return <p className="text-xs text-ink-subtle">Sem período anterior para comparar</p>;
  }
  if (d.diferenca === 0) {
    return (
      <p className="flex items-center justify-center gap-1 text-xs text-ink-muted">
        <span aria-hidden="true">{glifo(0)}</span>
        <span>Igual ao período anterior ({formatar(d.anterior)})</span>
      </p>
    );
  }
  const relativo = d.bps === null ? null : comSinal(formatPercentFromBps(Math.abs(d.bps)), d.diferenca);
  const absoluto = comSinal(formatar(Math.abs(d.diferenca)), d.diferenca);
  const direcao = d.diferenca > 0 ? 'subiu' : 'caiu';
  return (
    <p className={`flex flex-wrap items-center justify-center gap-x-1 text-xs tabular-nums ${classeDoDelta(d.diferenca, sentido)}`}>
      <span aria-hidden="true">{glifo(d.diferenca)}</span>
      <span className="sr-only">{direcao}</span>
      {relativo === null ? null : <strong className="font-semibold">{relativo}</strong>}
      {relativo === null ? null : <span aria-hidden="true">·</span>}
      <span>
        {absoluto} <span className="text-ink-subtle">vs. período anterior ({formatar(d.anterior)})</span>
      </span>
    </p>
  );
}

function KpiDoFunil({
  icon,
  tom,
  label,
  dica,
  value,
  support,
  linhas,
  delta: deltaNode,
}: {
  readonly icon: IconName;
  readonly tom: keyof typeof ICONE_TOM;
  readonly label: string;
  /** Explicacao em termos de negocio; vira `title` do rotulo. */
  readonly dica: string;
  readonly value: string;
  readonly support?: string;
  readonly linhas?: readonly LinhaDeCanal[];
  readonly delta: JSX.Element;
}): JSX.Element {
  return (
    <article className="flex flex-col items-center gap-2 rounded-2xl border border-line bg-surface px-5 py-6 text-center shadow-soft">
      <span aria-hidden="true" className={`grid h-9 w-9 place-items-center rounded-xl ${ICONE_TOM[tom]}`}>
        <Icon name={icon} size="md" />
      </span>
      <h3 className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted" title={dica}>
        {label}
        <Icon name="info" size="xs" className="text-ink-subtle" />
      </h3>
      <p className="text-3xl font-semibold tracking-tight text-ink">{value}</p>
      {deltaNode}
      {linhas !== undefined && linhas.length > 0 ? (
        <ul className="flex flex-col items-center gap-1 text-sm">
          {linhas.map((l) => (
            <li key={l.key} className="flex items-center gap-1.5 text-ink">
              <Dot tone={l.tone} />
              <Icon name={l.icon} size="xs" className="text-ink-muted" />
              <span className="tabular-nums font-medium">{l.texto}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {support === undefined ? null : <p className="max-w-[17rem] text-xs text-ink-subtle">{support}</p>}
    </article>
  );
}

/** Delta da taxa: "▲ de 9,1% para 11,4%". Compara bps, nunca taxa/taxa. */
function LinhaDeltaDaTaxa({ atualBps, anteriorBps }: { readonly atualBps: number | null; readonly anteriorBps: number | null }): JSX.Element {
  if (anteriorBps === null || atualBps === null) {
    return <p className="text-xs text-ink-subtle">{anteriorBps === null ? 'Sem período anterior para comparar' : 'Sem base na janela para comparar'}</p>;
  }
  const diferenca = atualBps - anteriorBps;
  return (
    <p className={`flex flex-wrap items-center justify-center gap-x-1 text-xs tabular-nums ${classeDoDelta(diferenca, 'maior-melhor')}`}>
      <span aria-hidden="true">{glifo(diferenca)}</span>
      <span className="sr-only">{diferenca > 0 ? 'subiu' : diferenca < 0 ? 'caiu' : 'igual'}</span>
      <span>
        de {formatPercentFromBps(anteriorBps)} para {formatPercentFromBps(atualBps)} <span className="text-ink-subtle">no período anterior</span>
      </span>
    </p>
  );
}

export function KpisDoFunil({ kind, metrics }: { readonly kind: FunnelKind; readonly metrics: OfferMetrics }): JSX.Element {
  const v = VOCABULARIO[kind];
  const canais = metrics.recoveredByChannel;
  const anterior = metrics.previous;

  // Expirados nao tem toque de voz: a linha "0 pela ligacao" so confundiria.
  const detalheRecuperados = CREDIT_CHANNELS.filter((c) => c !== 'voice' || kind === 'carrinho_abandonado')
    .map((c) => `${formatCount(canais[c])} ${CANAL[c].curto}`)
    .join(' · ');

  const linhasDeReceita: LinhaDeCanal[] = CREDIT_CHANNELS.filter((c) => metrics.recoveredAmountByChannelCents[c] > 0).map((c) => ({
    key: c,
    icon: CANAL[c].icon,
    tone: CANAL[c].tone,
    texto: formatBRLFromCents(metrics.recoveredAmountByChannelCents[c]),
  }));

  const totalComAnteriores = metrics.recoveredAmountCents + metrics.carryOverAmountCents;
  const liquido = totalComAnteriores - metrics.commissionCents;
  const taxas = taxasParaComparar(metrics);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiDoFunil
        icon={v.icone}
        tom="accent"
        label={v.entradaKpi}
        dica={kind === 'expirados' ? 'Assinaturas que chegaram ao fim no período e não foram renovadas. É a base de toda taxa desta tela.' : 'Pessoas que preencheram o checkout e ficaram 30 minutos sem pagar. É a base de toda taxa desta tela.'}
        value={formatCountCompact(metrics.entered)}
        delta={<LinhaDelta d={anterior === null ? null : delta(metrics.entered, anterior.entered)} sentido="neutro" formatar={formatCount} />}
        support={kind === 'expirados' && metrics.dueToday > 0 ? `${v.entradaApoio} · ${formatCount(metrics.dueToday)} vencem ainda hoje` : v.entradaApoio}
      />
      <KpiDoFunil
        icon="atualizar"
        tom="good"
        label={v.recuperadosKpi}
        dica="Pagaram pelo botão da mensagem (ou durante a ligação) ou responderam e a vendedora fechou em até 7 dias — venda creditada ao último contato (ADR-001)."
        value={formatCountCompact(metrics.recovered)}
        delta={<LinhaDelta d={anterior === null ? null : delta(metrics.recovered, anterior.recovered)} sentido="maior-melhor" formatar={formatCount} />}
        support={
          metrics.carryOverRecovered > 0
            ? `${detalheRecuperados} · mais ${formatCount(metrics.carryOverRecovered)} venda(s) no período de quem entrou antes dele`
            : detalheRecuperados
        }
      />
      <KpiDoFunil
        icon="grafico"
        tom="info"
        label="Taxa de recuperação"
        dica={`${v.recuperadosKpi} dividido por ${v.entradaKpi.toLowerCase()}, na mesma janela. Quem voltou sozinho antes do primeiro toque não entra.`}
        value={formatRatio(metrics.recovered, metrics.entered)}
        delta={<LinhaDeltaDaTaxa atualBps={taxas.atualBps} anteriorBps={taxas.anteriorBps} />}
        support={`${v.taxaApoio} (${formatCount(metrics.recovered)} de ${formatCount(metrics.entered)})`}
      />
      <KpiDoFunil
        icon="moeda"
        tom="brand"
        label="Receita recuperada"
        dica="Valor efetivamente pago pelas vendas recuperadas. A comissão do Converte Fácil é calculada sobre este valor, com o percentual congelado no momento da venda."
        value={formatBRLFromCents(metrics.recoveredAmountCents)}
        delta={<LinhaDelta d={anterior === null ? null : delta(metrics.recoveredAmountCents, anterior.recoveredAmountCents)} sentido="maior-melhor" formatar={formatBRLFromCents} />}
        linhas={linhasDeReceita}
        support={`Comissão do Converte Fácil ${formatBRLFromCents(metrics.commissionCents)} · líquido para a empresa ${formatBRLFromCents(liquido)}${
          metrics.carryOverAmountCents > 0 ? ` · inclui ${formatBRLFromCents(metrics.carryOverAmountCents)} de quem entrou antes do período` : ''
        }`}
      />
    </div>
  );
}
